import React from 'react';
import { Button } from '@mui/material';
import { Plus } from 'lucide-react';
import TreeDropdown, { TreeDropdownOption } from './tree-dropdown';
import AddCategoryPopup from './popup/add-category';
import useApi from '../hooks/use-api';
import routes from '../api/routes';
import buildApiRequestConfig from '../api/url-builder';
import { useUser } from '../providers/user-provider';
import { usePermissions } from '../providers/permissions-provider';
import { useToast } from '../providers/toaster-provider';

// Fired after a category is added from any picker, so every other picker on
// screen (e.g. one per contribution row) reloads and shows it too.
const CATEGORIES_CHANGED = 'fineants:categories-changed';

type Props = {
    value?: string | null;
    onChange: (categoryId: string) => void;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    height?: number;
    width?: number | string;
    disabled?: boolean;
};

// The company's categories as a tree, with "Add category" under the list for
// users who can create categories - the new category is selected once saved.
const CategoryPicker: React.FC<Props> = ({ value, onChange, placeholder = 'Select a category', error, helperText, height = 40, width = '100%', disabled }) => {
    const { user } = useUser();
    const { can } = usePermissions();
    const { showError, showSuccess } = useToast();
    const [categories, setCategories] = React.useState<any[]>([]);
    const [addOpen, setAddOpen] = React.useState(false);
    const [fetchCategories, setFetchCategories] = useApi();
    const [createCategory, setCreateCategory] = useApi();

    const load = React.useCallback(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchCategories({ url, method });
    }, [user?.entityId, setFetchCategories]);

    React.useEffect(() => {
        load();
        window.addEventListener(CATEGORIES_CHANGED, load);
        return () => window.removeEventListener(CATEGORIES_CHANGED, load);
    }, [load]);

    React.useEffect(() => {
        const { data, isLoading } = fetchCategories;
        if (data && !isLoading) setCategories(Array.isArray(data) ? data : []);
    }, [fetchCategories]);

    React.useEffect(() => {
        const { data, isLoading, error: createError } = createCategory;
        if (data && !isLoading) {
            showSuccess('Category created successfully');
            setAddOpen(false);
            if (data.id) onChange(String(data.id));
            window.dispatchEvent(new Event(CATEGORIES_CHANGED));
        }
        if (createError) showError(typeof createError === 'string' ? createError : 'Failed to create category');
    }, [createCategory]); // eslint-disable-line react-hooks/exhaustive-deps

    const options = React.useMemo(() => {
        const toOption = (cat: any): TreeDropdownOption => ({
            id: String(cat.id),
            label: cat.name,
            children: cat.children?.length ? cat.children.map(toOption) : undefined,
        });
        return categories.map(toOption);
    }, [categories]);

    // For AddCategoryPopup's duplicate code/name/search-term checks.
    const takenData = React.useMemo(() => {
        const flat: { code: string; name: string; searchStrings: string }[] = [];
        const walk = (cats: any[]) => cats.forEach(c => {
            flat.push({ code: c.code, name: c.name, searchStrings: c.searchString || '' });
            if (c.children?.length) walk(c.children);
        });
        walk(categories);
        return flat;
    }, [categories]);

    return (
        <>
            <TreeDropdown
                options={options}
                value={value ? String(value) : null}
                onChange={(id) => onChange(id || '')}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                height={height}
                width={width}
                disabled={disabled}
                footer={can('categories', 'create') ? (close) => (
                    <Button
                        fullWidth
                        size="small"
                        startIcon={<Plus size={16} />}
                        onClick={() => { close(); setAddOpen(true); }}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, color: 'var(--color-primary)' }}
                    >
                        Add category
                    </Button>
                ) : undefined}
            />
            <AddCategoryPopup
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onCreate={(categoryData) => {
                    const { url, method } = buildApiRequestConfig(routes.categories.create);
                    setCreateCategory({ url, method, body: { ...categoryData, entityId: user?.entityId, searchString: categoryData.searchStrings } });
                }}
                isEdit={{ id: null, state: false }}
                isChild={false}
                parent={null}
                takenData={takenData}
                entityId={user?.entityId}
            />
        </>
    );
};

export default CategoryPicker;
